import {
  alpha,
  Autocomplete,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { Iconify, RHFTextField } from '@app/components';
import AddBackgroundAndBoard from './add-background-and-board';
import { useDialog, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { CustomAvatar, CustomAvatarGroup } from '@app/components/custom-avatar';
import { selectSelectedProjectId } from '@app/modules/common';
import { selectMyProfileFormState } from '@app/modules/my-profile';
import { SvgColor } from '@app/components/svg-color';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { useAuthRolePermissions } from '@app/utils';
import { IMember, IProject } from '../types';
import { selectBoardDetailsState, selectSearchBoardState, selectTaskBoardState } from '../selector';
import { getBoardDetails, getSearchBoard, getTaskBoard } from '../slice';
import { BoardChangeDialog } from './board-change-dialog';
import { FilterDialog } from './filter-dialog';
interface props {
  id: number | null;
  filters: {
    overdue: boolean;
    unassigned: boolean;
    noDate: boolean;
    assignedToMe: boolean;
    noLabel: boolean;
    selectedMember: any;
    selectedLabel: any;
    searchKeyword: any;
  };
  setFilters: (filters: any) => void;
}
const TopBar = ({ id, filters, setFilters }: props) => {
  const boardDialog = useDialog();
  const boardChangeDialog = useDialog();
  const theme = useTheme();
  const dispatch = useDispatch();
  const filterDialog = useDialog();
  const navigate = useNavigate();
  const [query, setQuery] = useState<string>();
  const location = useLocation();

  const selectedProjectId = useSelector(selectSelectedProjectId);

  const { hasPermission } = useAuthRolePermissions();
  const canAddBoard = hasPermission('Task Board', 'w');

  const { data: userData } = useRequestState({
    stateSelector: selectMyProfileFormState,
    errorShown: true,
  });

  useEffect(() => {
    if (userData) {
      dispatch(getTaskBoard({ userId: userData?.id }));
    }
  }, [dispatch, userData]);

  useEffect(
    debounce(() => {
      if (query && query?.length > 0) {
        dispatch(getSearchBoard({ userId: userData?.id, name: query }));
      } else if (userData?.id ?? query?.length === 0) {
        dispatch(getSearchBoard({ userId: userData?.id }));
      }
    }, 1000),
    [dispatch, userData]
  );

  const { data } = useRequestState({
    stateSelector: selectTaskBoardState,
    errorShown: true,
  });
  const { data: taskDetail, loading } = useRequestState({
    stateSelector: selectBoardDetailsState,
    errorShown: true,
  });

  const { data: searchBoard } = useRequestState({
    stateSelector: selectSearchBoardState,
    errorShown: true,
  });
  const [title, setTitle] = useState('');
  const [setBoardData] = useState();

  const handleClick = (event: string) => {
    setTitle(event);
    boardDialog.show();
  };

  const [selectedProject, setSelectedProject] = useState<IProject | undefined>();

  useEffect(() => {
    const locationId = location?.state?.id;

    if (locationId && data?.length) {
      const selectedBoard = data.find((board: IProject) => board.id === locationId);
      if (selectedBoard) {
        setSelectedProject(selectedBoard);
        dispatch(getBoardDetails(selectedBoard.id));
      } else {
        dispatch(getBoardDetails(locationId));
      }
    } else if (selectedProjectId && selectedProject === undefined) {
      dispatch(getBoardDetails(selectedProjectId));
    } else if (selectedProject) {
      dispatch(getBoardDetails(selectedProject.id));
    } else if (data && data.length > 0) {
      setSelectedProject(data[0]);
    }
  }, [data, dispatch, selectedProject, selectedProjectId, location?.state?.id]);

  const handleBoardSelect = (event: React.SyntheticEvent, value: any) => {
    dispatch(getBoardDetails(value?.value));
    const selectedData = data?.filter((item: IProject) => item?.id === value?.value);
    setSelectedProject(selectedData[0]);
    setQuery(value);
  };

  const showTaskListPage = () => {
    navigate(PATH_DASHBOARD.tasksList, { state: { id } });
  };

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: alpha(theme.palette.common.white, 0.4),
        backdropFilter: 'blur(5px)',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      py={0.5}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          flexWrap: 'wrap',
          mx: 2,
        }}
        gap={{ md: 4, xs: 2 }}
      >
        <Stack
          sx={{
            backgroundColor: '#3e3a41',
            borderRadius: '12px',
            px: 1,
            py: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontSize: '15px',
              fontWeight: 600,
            }}
          >
            {taskDetail?.projectName}
          </Typography>
        </Stack>

        <Stack sx={{ justifyContent: 'center', alignItems: 'center' }}>
          <Tooltip title={taskDetail?.name}>
            <Typography
              sx={{
                color: theme.palette.taskboard.raisinBlack,
                fontSize: '15px',
                fontWeight: '600',
              }}
            >
              {taskDetail?.name.slice(0, 10)}...
            </Typography>
          </Tooltip>
        </Stack>
        {canAddBoard && (
          <Stack
            onClick={() => {
              handleClick('Create Board');
            }}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'row',
              cursor: 'pointer',
            }}
          >
            <>
              <Typography
                sx={{
                  color: theme.palette.taskboard.raisinBlack,
                  fontSize: '15px',
                  fontWeight: '600',
                }}
              >
                {Strings.taskBoard.createBoard}
              </Typography>
              <IconButton
                aria-label="plus"
                sx={{
                  color: theme.palette.taskboard.raisinBlack,
                }}
              >
                <Iconify icon={'mdi:plus'} />
              </IconButton>
            </>
          </Stack>
        )}

        <Stack sx={{ display: { md: 'flex', xs: 'none' } }}>
          <Stack>
            <CustomAvatarGroup max={6} sx={{ pt: 0.5 }}>
              {taskDetail?.members?.length > 0 &&
                taskDetail?.members?.map((member: IMember, index: number) => (
                  <CustomAvatar
                    style={{
                      backgroundColor: theme.palette.taskboard.raisinBlack,
                      color: theme.palette.common.white,
                    }}
                    sx={{
                      '& .MuiAvatarGroup-avatar': {
                        backgroundColor: theme.palette.taskboard.raisinBlack,
                        color: theme.palette.common.white,
                        '&:first-of-type': {
                          backgroundColor: theme.palette.taskboard.raisinBlack,
                          color: theme.palette.common.white,
                        },
                        '&:last-of-type': {
                          backgroundColor: theme.palette.taskboard.raisinBlack,
                          color: theme.palette.common.white,
                        },
                      },
                    }}
                    src={member?.profileImage}
                    key={index}
                    name={`${member?.firstName} ${member?.lastName}`}
                  />
                ))}
            </CustomAvatarGroup>
          </Stack>
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Stack
            sx={{
              flex: 1,
            }}
          >
            <Autocomplete
              options={
                searchBoard?.records?.map((option: IProject) => ({
                  label: option?.name,
                  value: option.id,
                })) ?? []
              }
              onChange={handleBoardSelect}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  padding: 0,
                  '&:hover fieldset': {
                    borderColor: theme.palette.taskboard.raisinBlack,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.taskboard.raisinBlack,
                  },
                },
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0 8px',
                },
                '& .MuiAutocomplete-input': {
                  padding: '8px 0 !important',
                },
                '& .MuiFormLabel-root': {
                  color: theme.palette.taskboard.raisinBlack,
                },
              }}
              renderInput={(params: any) => (
                <RHFTextField
                  name="searchBar"
                  placeholder="Search Board"
                  {...params}
                  onChange={(event: any) => {
                    setQuery(event.target.value);
                  }}
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.palette.taskboard.raisinBlack,
                    },
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack alignItems={'center'} flexDirection={'row'} gap={1}>
          <Stack
            height={'100%'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Tooltip title="List View">
              <SvgColor
                src={`/assets/icons/card/ic_taskList.svg`}
                onClick={showTaskListPage}
                color={theme.palette.taskboard.raisinBlack}
              />
            </Tooltip>
          </Stack>
          <Stack
            height={'100%'}
            width={25}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              filter: 'brightness(30%)',
              cursor: 'pointer',
            }}
            onClick={filterDialog.show}
          >
            <img src={`/assets/icons/navbar/filter.svg`} alt="back" />
          </Stack>
        </Stack>
      </Stack>
      {loading && (
        <LinearProgress
          sx={{
            height: 3,
            mt: 1,
            backgroundColor: theme.palette.grey[300],
            '& .MuiLinearProgress-bar': {
              backgroundColor: theme.palette.taskboard.darkSlateGray,
            },
          }}
        />
      )}

      {boardChangeDialog.visible && (
        <BoardChangeDialog
          open
          onClose={boardChangeDialog.hide}
          setSelectedProject={setSelectedProject}
          openBoardId={id}
        />
      )}
      {boardDialog.visible && (
        <AddBackgroundAndBoard
          title={title}
          open
          onClose={boardDialog.hide}
          setBoardData={setBoardData}
        />
      )}
      {filterDialog.visible && (
        <FilterDialog open onClose={filterDialog.hide} filters={filters} setFilters={setFilters} />
      )}
    </Stack>
  );
};

export { TopBar };
